import type maplibregl from "maplibre-gl";
import type { Palette } from "@designboticteam/core";
import {
	findParcelNumericField,
	type LayerStyleStates,
	type MapStoreService,
	PARCEL_COLOR_HEIGHT_DIFF_KEY,
	PARCEL_COLOR_PRZEZNACZENIE_DIFF_KEY,
	PARCEL_COLOR_PUM_DIFF_KEY,
	type ParcelNumericField,
} from "@designboticteam/map";
import type { MapLibreInstanceService } from "../../../core/MapLibreInstanceService";
import { buildCaseExpression } from "../../../rendering/PmTilesRenderingService/utils/expressionBuilders/buildCaseExpression";
import { buildInterpolateExpression } from "../../../rendering/PmTilesRenderingService/utils/expressionBuilders/buildInterpolateExpression";
import { createStyleFillPaint } from "../../../rendering/PmTilesRenderingService/utils/style/createStyleFillLayer";
import {
	PARCELS_EXTRUSION_LAYER_ID,
	PARCELS_FILL_LAYER_ID,
	PARCELS_LABELS_LAYER_ID,
	PARCELS_OUTLINE_LAYER_ID,
	PARCELS_PMTILES_SOURCE_ID,
	PARCELS_SOURCE_LAYER,
} from "../constants";
import { createParcelsBaseStyle } from "./createParcelsSourceDefinition";

const IS_HOVERED = ["boolean", ["feature-state", "hover"], false] as unknown as maplibregl.ExpressionSpecification;
const MATCHES_FILTER = ["boolean", ["feature-state", "matchesFilter"], false] as unknown as maplibregl.ExpressionSpecification;

// Both plans collapse their per-zone heights into a [min, max] span per parcel; the comparison reads the
// upper end of each, matching how the numeric-field pickers treat the POG metrics.
const POG_HEIGHT_PROPERTY = "pog__maksWysokoscZabudowy_max";
const MPZP_HEIGHT_PROPERTY = "mpzp__wysokoscMaxM_max";
const PRZEZNACZENIE_ZGODNOSC_PROPERTY = "przeznaczenie__zgodnosc";

// In a difference mode a parcel gets a colour only when both sides carry a value and the two disagree.
// A missing value must not fall back to 0, because that would look like a real difference.
function buildHasDifference(pogProperty: string, mpzpProperty: string): maplibregl.ExpressionSpecification {
	return [
		"all",
		["has", pogProperty],
		["has", mpzpProperty],
		["!=", ["to-number", ["get", pogProperty]], ["to-number", ["get", mpzpProperty]]],
	] as unknown as maplibregl.ExpressionSpecification;
}

const HAS_PUM_DIFFERENCE = buildHasDifference("pog__pum", "mpzp__pum");
const HAS_HEIGHT_DIFFERENCE = buildHasDifference(POG_HEIGHT_PROPERTY, MPZP_HEIGHT_PROPERTY);

// The pipeline resolves both plans onto the ustawa's strefa symbols and stores 1 for agreement, 0 for a
// conflict, nothing where the two cannot be compared. Only a conflict is worth showing.
const HAS_PRZEZNACZENIE_DIFFERENCE = [
	"all",
	["has", PRZEZNACZENIE_ZGODNOSC_PROPERTY],
	["==", ["to-number", ["get", PRZEZNACZENIE_ZGODNOSC_PROPERTY]], 0],
] as unknown as maplibregl.ExpressionSpecification;

// The base style has idle opacity 0, so a colour mode has to raise it or the gradient stays invisible.
const COLORED_FILL_OPACITY = buildCaseExpression({
	cases: [
		[IS_HOVERED, 1],
		[MATCHES_FILTER, 0.85],
	],
	fallback: 0.65,
});

// The same, except that parcels with no difference to show stay invisible, as in the base style.
function buildDiffFillOpacity(hasDifference: maplibregl.ExpressionSpecification) {
	return buildCaseExpression({
		cases: [
			[IS_HOVERED, 1],
			[MATCHES_FILTER, 0.85],
			[hasDifference, 0.65],
		],
		fallback: 0,
	});
}

// A parcel no MPZP covers. The enhance writes a designation for every parcel that touched a terrain, so
// its absence is what marks land outside any plan. mpzp__pum would be wrong here: it exists only where
// the uchwała gave an intensity, which also leaves out roads and terrains without parameters.
const IS_OUTSIDE_MPZP = ["!", ["has", "mpzp__przeznaczenie"]] as unknown as maplibregl.ExpressionSpecification;

const OUTSIDE_MPZP_COLOR = "#d32f2f";

// Opaque enough to read as a warning at idle, while hover and filter matches still stand out.
const OUTSIDE_MPZP_FILL_OPACITY = buildCaseExpression({
	cases: [
		[IS_HOVERED, 1],
		[MATCHES_FILTER, 0.85],
	],
	fallback: 0.55,
});

const COLOR_FROM = "#ffe0a3";
const COLOR_TO = "#b91c1c";

// Almost every parcel sits near 0, but a few reach 251k m² (median 0, p95 ≈ 8k). The ramp uses signed
// log10 so that the small differences stay visible, in the same way as the area fields.
const PUM_DIFF_MAX = 251_000;

// The tallest POG height in the dataset, so the ramp saturates only at the most extreme disagreement
// the two plans can produce.
const HEIGHT_DIFF_MAX_M = 130;

// One flat colour, because the designation comparison is a yes/no answer with no magnitude to shade.
// Deliberately not the missing-data red, so the two overlays stay tellable apart when both are on.
const PRZEZNACZENIE_DIFF_COLOR = "#6d28d9";

const MAX_HEIGHT_M = 500;
const EXTRUSION_OPACITY = 0.85;

const LABELS_MINZOOM = 15;
const LABELS_FALLBACK_FONT = ["Noto Sans Regular"];

type FillPaint = NonNullable<maplibregl.FillLayerSpecification["paint"]>;
type ParcelFillPaint = { color: FillPaint["fill-color"]; opacity: FillPaint["fill-opacity"] };

type ParcelStyleControllerParams = {
	mapLibreInstanceService: MapLibreInstanceService;
	mapStoreService: MapStoreService;
	palette: Palette;
};

/**
 * How a parcel is drawn. Colour and height are independent channels over the same numeric fields.
 *
 *   colorField
 *       │
 *       ├── null ────────────→  colour   tertiary, straight from the base style
 *       │                       opacity  idle 0 · matched 0.5 · hover 0.5
 *       │
 *       ├── numeric field ───→  colour   ramp #ffe0a3 → #b91c1c over the field value
 *       │                       opacity  idle 0.65 · matched 0.85 · hover 1
 *       │
 *       ├── "pum__diff" ─────→  colour   ramp between the two colours the user picked
 *       │                       opacity  idle 0.65 · matched 0.85 · hover 1
 *       │                                idle drops to 0 when the parcel has no real difference
 *       │
 *       ├── "height__diff" ──→  the same, over the two maximum building heights
 *       │
 *       └── "przeznaczenie__diff"
 *                           ──→  colour   one flat violet where the two plans disagree on land use
 *                               opacity  as above; idle drops to 0 when they agree or cannot be compared
 *
 *   heightField
 *       │
 *       ├── null ────────────→  the flat __fill is visible, __extrusion stays hidden
 *       │
 *       └── numeric field ───→  __extrusion 0…500 m, flat __fill hidden, colour from the branch above
 *                               reads no feature-state, so hover and matched do not show on the solid
 *
 * __outline and __labels are never coloured here.
 */
class ParcelStyleController {
	private readonly mapLibreInstanceService: MapLibreInstanceService;
	private readonly mapStoreService: MapStoreService;
	private readonly palette: Palette;
	private readonly baseStyle: LayerStyleStates;

	constructor({ mapLibreInstanceService, mapStoreService, palette }: ParcelStyleControllerParams) {
		this.mapLibreInstanceService = mapLibreInstanceService;
		this.mapStoreService = mapStoreService;
		this.palette = palette;
		this.baseStyle = createParcelsBaseStyle(palette);
	}

	/** Repaints the parcel layers for the current visualization state, and is safe to call again. */
	public apply(): void {
		const map = this.mapLibreInstanceService.map;

		if (map.getLayer(PARCELS_FILL_LAYER_ID) === undefined) {
			return;
		}

		const { heightField, colorField, highlightParcelsOutsideMpzp } = this.mapStoreService.state.parcels.visualization;
		const heightFieldDefinition = findParcelNumericField(heightField);
		const colorExpression = this.buildColorExpression(colorField);

		const modeFill = colorExpression === null ? this.baseFillPaint() : { color: colorExpression, opacity: this.buildFillOpacity(colorField) };

		const fill = highlightParcelsOutsideMpzp === true ? this.withParcelsOutsideMpzp(modeFill) : modeFill;

		map.setPaintProperty(PARCELS_FILL_LAYER_ID, "fill-color", fill.color);
		map.setPaintProperty(PARCELS_FILL_LAYER_ID, "fill-opacity", fill.opacity);

		// With 3D height on, hide the flat fill.
		map.setLayoutProperty(PARCELS_FILL_LAYER_ID, "visibility", heightFieldDefinition !== null ? "none" : "visible");

		this.applyExtrusion(heightFieldDefinition, highlightParcelsOutsideMpzp === true ? fill.color : colorExpression);
		this.applyLabels();
	}

	/** Creates the parcel-number labels layer on first use and keeps its visibility in sync with the store. */
	public applyLabels(): void {
		const map = this.mapLibreInstanceService.map;

		if (map.getLayer(PARCELS_FILL_LAYER_ID) === undefined) {
			return;
		}

		if (map.getLayer(PARCELS_LABELS_LAYER_ID) === undefined) {
			map.addLayer({
				id: PARCELS_LABELS_LAYER_ID,
				source: PARCELS_PMTILES_SOURCE_ID,
				"source-layer": PARCELS_SOURCE_LAYER,
				type: "symbol",
				minzoom: LABELS_MINZOOM,
				layout: {
					"text-field": ["get", "parcel__numer_dzialki"],
					"text-font": this.resolveLabelFont(),
					"text-size": ["interpolate", ["linear"], ["zoom"], 15, 10, 18, 13],
					"text-padding": 2,
				},
				paint: {
					"text-color": this.palette.main.tertiary,
					"text-halo-color": "#ffffff",
					"text-halo-width": 1.2,
				},
			});
		}

		map.setLayoutProperty(PARCELS_LABELS_LAYER_ID, "visibility", this.mapStoreService.state.layers.parcelLabels ? "visible" : "none");

		this.raiseLayers();
	}

	/** Keeps the parcel layers above POG and MPZP, no matter which archive finished loading first. */
	public readonly raiseLayers = (): void => {
		const map = this.mapLibreInstanceService.map;

		// The list goes from bottom to top, because `moveLayer` without `beforeId` moves a layer to the top.
		for (const layerId of [PARCELS_FILL_LAYER_ID, PARCELS_OUTLINE_LAYER_ID, PARCELS_EXTRUSION_LAYER_ID, PARCELS_LABELS_LAYER_ID]) {
			if (map.getLayer(layerId) !== undefined) {
				map.moveLayer(layerId);
			}
		}
	};

	/** Turns the active colour mode into a fill expression, or returns null when no mode is on. */
	private buildColorExpression(colorField: string | null): maplibregl.ExpressionSpecification | null {
		const { pumDiffColors, heightDiffColors } = this.mapStoreService.state.parcels.visualization;

		if (colorField === PARCEL_COLOR_PUM_DIFF_KEY) {
			return this.buildDiffColorExpression({ pogProperty: "pog__pum", mpzpProperty: "mpzp__pum", max: PUM_DIFF_MAX, colors: pumDiffColors });
		}

		if (colorField === PARCEL_COLOR_HEIGHT_DIFF_KEY) {
			return this.buildDiffColorExpression({
				pogProperty: POG_HEIGHT_PROPERTY,
				mpzpProperty: MPZP_HEIGHT_PROPERTY,
				max: HEIGHT_DIFF_MAX_M,
				colors: heightDiffColors,
			});
		}

		if (colorField === PARCEL_COLOR_PRZEZNACZENIE_DIFF_KEY) {
			return this.buildPrzeznaczenieDiffColorExpression();
		}

		const field = findParcelNumericField(colorField);

		if (field === null) {
			return null;
		}

		return buildInterpolateExpression({
			attribute: field.tileProperty,
			min: field.min,
			max: field.max,
			from: COLOR_FROM,
			to: COLOR_TO,
			scale: field.scale,
		});
	}

	/** Colour ramp running from the MPZP colour at −max, through transparent white at 0, to the POG colour at +max. */
	private buildDiffColorExpression({
		pogProperty,
		mpzpProperty,
		max,
		colors,
	}: {
		pogProperty: string;
		mpzpProperty: string;
		max: number;
		colors: { pog: string; mpzp: string };
	}): maplibregl.ExpressionSpecification {
		const neutral = this.palette.main.tertiary;
		const diff = ["-", ["to-number", ["get", pogProperty]], ["to-number", ["get", mpzpProperty]]];

		// Signed log10, copied from the log branch of buildInterpolateExpression. That function cannot be
		// reused here, because the input is a difference between two fields, not a single attribute.
		// The compression is what makes the ramp usable: the midpoint is transparent, so on a linear ramp
		// the ordinary small difference would land near it and come out invisible.
		const input = ["*", ["case", [">", diff, 0], 1, -1], ["log10", ["max", 1, ["abs", diff]]]];
		const high = Math.log10(max);

		return [
			"case",
			["!", ["all", ["has", pogProperty], ["has", mpzpProperty]]],
			neutral,
			["interpolate", ["linear"], input, -high, colors.mpzp, 0, "rgba(255, 255, 255, 0)", high, colors.pog],
		] as unknown as maplibregl.ExpressionSpecification;
	}

	/** Two-valued, so no ramp: only a conflict gets a colour, agreement and the incomparable stay clear. */
	private buildPrzeznaczenieDiffColorExpression(): maplibregl.ExpressionSpecification {
		return [
			"case",
			["!", ["has", PRZEZNACZENIE_ZGODNOSC_PROPERTY]],
			this.palette.main.tertiary,
			HAS_PRZEZNACZENIE_DIFFERENCE,
			PRZEZNACZENIE_DIFF_COLOR,
			"rgba(255, 255, 255, 0)",
		] as unknown as maplibregl.ExpressionSpecification;
	}

	/** How far the active colour mode lifts the fill out of the base style's zero idle opacity. */
	private buildFillOpacity(colorField: string | null): FillPaint["fill-opacity"] {
		if (colorField === PARCEL_COLOR_PUM_DIFF_KEY) {
			return buildDiffFillOpacity(HAS_PUM_DIFFERENCE);
		}

		if (colorField === PARCEL_COLOR_HEIGHT_DIFF_KEY) {
			return buildDiffFillOpacity(HAS_HEIGHT_DIFFERENCE);
		}

		if (colorField === PARCEL_COLOR_PRZEZNACZENIE_DIFF_KEY) {
			return buildDiffFillOpacity(HAS_PRZEZNACZENIE_DIFFERENCE);
		}

		return COLORED_FILL_OPACITY;
	}

	/** The plain tint from the base style, used whenever no colour mode is on. */
	private baseFillPaint(): ParcelFillPaint {
		if (this.baseStyle.idle.fill === undefined) {
			return { color: undefined, opacity: undefined };
		}

		const paint = createStyleFillPaint({
			sourceId: PARCELS_PMTILES_SOURCE_ID,
			idleFill: this.baseStyle.idle.fill,
			matchedFill: this.baseStyle.matched?.fill,
			hoverFill: this.baseStyle.hover.fill,
		});

		return { color: paint?.["fill-color"], opacity: paint?.["fill-opacity"] };
	}

	/** Overrides only the parcels outside any MPZP, so the active colour mode keeps painting the rest. */
	private withParcelsOutsideMpzp(fill: ParcelFillPaint): ParcelFillPaint {
		return {
			color: ["case", IS_OUTSIDE_MPZP, OUTSIDE_MPZP_COLOR, fill.color ?? this.palette.main.tertiary] as unknown as maplibregl.ExpressionSpecification,
			opacity: ["case", IS_OUTSIDE_MPZP, OUTSIDE_MPZP_FILL_OPACITY, fill.opacity ?? 0] as unknown as maplibregl.ExpressionSpecification,
		};
	}

	/** Updates the 3D height layer, and hides it when no height field is selected. */
	private applyExtrusion(heightFieldDefinition: ParcelNumericField | null, colorExpression: ParcelFillPaint["color"] | null): void {
		const map = this.mapLibreInstanceService.map;

		if (heightFieldDefinition === null) {
			if (map.getLayer(PARCELS_EXTRUSION_LAYER_ID) !== undefined) {
				map.setLayoutProperty(PARCELS_EXTRUSION_LAYER_ID, "visibility", "none");
			}

			return;
		}

		if (map.getLayer(PARCELS_EXTRUSION_LAYER_ID) === undefined) {
			map.addLayer({
				id: PARCELS_EXTRUSION_LAYER_ID,
				source: PARCELS_PMTILES_SOURCE_ID,
				"source-layer": PARCELS_SOURCE_LAYER,
				type: "fill-extrusion",
				paint: { "fill-extrusion-opacity": EXTRUSION_OPACITY },
			});
		}

		map.setLayoutProperty(PARCELS_EXTRUSION_LAYER_ID, "visibility", "visible");
		map.setPaintProperty(PARCELS_EXTRUSION_LAYER_ID, "fill-extrusion-color", colorExpression ?? this.palette.main.tertiary);
		map.setPaintProperty(PARCELS_EXTRUSION_LAYER_ID, "fill-extrusion-height", this.buildExtrusionHeight(heightFieldDefinition));
	}

	/**
	 * A field already in metres is drawn at its own value times the exaggeration, so a 26 m limit reads as
	 * 26 m at factor 1. Every other field is an area, a ratio or a percentage, where metres would mean
	 * nothing, so those keep their normalized mapping onto a fixed range.
	 */
	private buildExtrusionHeight(field: ParcelNumericField): maplibregl.ExpressionSpecification {
		if (field.unit === "m") {
			const { heightFactor } = this.mapStoreService.state.parcels.visualization;

			return ["*", ["to-number", ["coalesce", ["get", field.tileProperty], 0]], heightFactor] as unknown as maplibregl.ExpressionSpecification;
		}

		return buildInterpolateExpression({
			attribute: field.tileProperty,
			min: field.min,
			max: field.max,
			from: 0,
			to: MAX_HEIGHT_M,
			scale: field.scale,
		});
	}

	/** Reuses a font from the basemap, because its glyph endpoint only serves fonts it already knows. */
	private resolveLabelFont(): Array<string> {
		const symbolLayer = this.mapLibreInstanceService.map.getStyle().layers.find((layer) => layer.type === "symbol");
		const font = symbolLayer?.type === "symbol" ? symbolLayer.layout?.["text-font"] : undefined;

		return Array.isArray(font) ? (font as Array<string>) : LABELS_FALLBACK_FONT;
	}
}

export { ParcelStyleController };
